/**
 * CompanyDashboardPage — owner view of the user's shipping company.
 * Mirrors Flutter `company/profile_page.dart`.
 *
 *   no company   → CTA to register
 *   draft        → KYC docs + submit-for-review
 *   pending      → "awaiting admin" banner + read-only edit
 *   approved     → capability-driven tile grid (members, branded shipment,
 *                  warehouse-inbound, container management, trips, …)
 *   rejected     → reason banner + "edit & resubmit" CTA
 *   suspended    → support-contact CTA
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Building2, AlertCircle, CheckCircle, FileText, Loader, Plus,
  ShieldCheck, Send, Hash, Pause, Edit, Users, Warehouse, Ship, Truck,
  MessageCircle, DollarSign, Layers,
} from 'lucide-react';
import { useMyCompany, useSubmitCompany, useUploadKycDoc } from '@/hooks/useCompany';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/common/Button';
import type { CompanyKycDoc, KycDocKind } from '@/types/company';
import { useState } from 'react';

// DB constraint allows 7 statuses (draft / pending_approval / approved /
// rejected / suspended / revoked / unclaimed). Keep this map in sync —
// and never assume the lookup hits: a future status would crash render
// at `cfg.color` without the fallback below.
const STATUS_LABEL: Record<string, { label: string; color: string }> = {
  draft:            { label: 'مسوّدة',           color: '#6B7280' },
  pending_approval: { label: 'قيد المراجعة',    color: '#F59E0B' },
  approved:         { label: 'معتمَدة ✓',       color: '#16A34A' },
  rejected:         { label: 'مرفوضة',          color: '#DC2626' },
  suspended:        { label: 'موقوفة مؤقتاً',   color: '#7C3AED' },
  revoked:          { label: 'مُلغاة',           color: '#991B1B' },
  unclaimed:        { label: 'غير مُطالَب بها', color: '#9CA3AF' },
};
const STATUS_FALLBACK = { label: '—', color: '#6B7280' };

const REQUIRED_DOCS_LOCAL: KycDocKind[] = ['commercial_register', 'tax_certificate', 'owner_id_front', 'owner_id_back'];
const REQUIRED_DOCS_INTL: KycDocKind[] = ['commercial_register', 'tax_certificate', 'import_license', 'owner_id_front', 'owner_id_back'];
const DOC_LABELS: Record<KycDocKind, string> = {
  commercial_register: 'السجل التجاري',
  tax_certificate:     'الشهادة الضريبية',
  import_license:      'رخصة الاستيراد',
  owner_id_front:      'هوية المالك (وجه)',
  owner_id_back:       'هوية المالك (خلف)',
  bank_certificate:    'شهادة بنكية',
  other:               'أخرى',
};

export default function CompanyDashboardPage() {
  const { t } = useTranslation();
  const { data: company, isLoading } = useMyCompany();
  const submitMut = useSubmitCompany();

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex justify-center py-16 text-text-tertiary">
          <Loader size={32} className="animate-spin" />
        </div>
      </AppLayout>
    );
  }

  if (!company) {
    return (
      <AppLayout>
        <div className="bg-surface border border-border rounded-2xl p-10 text-center max-w-lg mx-auto">
          <Building2 size={56} className="mx-auto text-primary mb-4" />
          <h1 className="text-xl font-bold text-text-primary mb-2">
            {t('no_company_yet', 'سجِّل شركة شحن')}
          </h1>
          <p className="text-text-secondary mb-5">
            {t('register_company_pitch', 'حوّل عملك لكيان رسمي معتمَد. شحناتك ستظهر باسم شركتك بعد الموافقة.')}
          </p>
          <Link
            to="/company/register"
            className="inline-flex items-center gap-2 bg-primary text-white rounded-xl px-6 py-3 text-sm font-semibold hover:bg-primary-light"
          >
            <Plus size={16} /> {t('register_company', 'تسجيل شركة شحن')}
          </Link>
        </div>
      </AppLayout>
    );
  }

  const cfg = STATUS_LABEL[company.status] ?? STATUS_FALLBACK;
  const required = company.company_type === 'international' ? REQUIRED_DOCS_INTL : REQUIRED_DOCS_LOCAL;
  const uploaded = new Set((company.kyc_docs || []).map((d) => d.kind));
  const allUploaded = required.every((k) => uploaded.has(k));
  const caps = new Set(company.active_capabilities || []);

  async function handleSubmit() {
    if (!allUploaded) return;
    try { await submitMut.mutateAsync(company!.id); }
    catch (e: any) { alert(e?.message || 'فشل التقديم'); }
  }

  return (
    <AppLayout>
      {/* Header */}
      <div className="bg-surface border border-border rounded-2xl p-5 mb-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Building2 size={18} className="text-primary" />
              <h1 className="text-xl font-bold text-text-primary">{company.name_ar || company.name}</h1>
            </div>
            <div className="flex items-center gap-3 text-text-secondary text-sm flex-wrap">
              <span className="flex items-center gap-1"><Hash size={12} /> {company.public_code}</span>
              <span>· {company.company_type === 'international' ? 'دولية' : 'محلية'}</span>
              <span>· {company.operator_mode === 'shipper' ? 'شاحن' : company.operator_mode === 'operator' ? 'مشغّل' : 'كلاهما'}</span>
            </div>
          </div>
          <span
            className="rounded-full px-3 py-1 text-xs font-semibold whitespace-nowrap"
            style={{ background: `${cfg.color}22`, color: cfg.color }}
          >
            {t(`company_status.${company.status}`, cfg.label)}
          </span>
        </div>

        {/* Capabilities */}
        {company.status === 'approved' && (company.active_capabilities?.length ?? 0) > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {company.active_capabilities!.map((cap) => (
              <span key={cap} className="bg-success/10 text-success rounded-full px-3 py-1 text-xs flex items-center gap-1">
                <ShieldCheck size={12} /> {t(`capability.${cap}`, cap)}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Status-specific banners */}
      {company.status === 'pending_approval' && (
        <div className="bg-warning/10 border border-warning/30 rounded-2xl p-4 mb-4 flex items-start gap-2 text-warning">
          <Loader size={18} className="mt-0.5 animate-spin" />
          <div>
            <div className="font-bold">{t('pending_admin_review', 'تم التقديم — بانتظار المراجعة')}</div>
            <div className="text-sm">
              {t('pending_review_hint', 'الإدارة تراجع وثائقك خلال ٢٤–٤٨ ساعة. سيصلك إشعار بالنتيجة.')}
            </div>
          </div>
        </div>
      )}
      {company.status === 'rejected' && company.rejection_reason && (
        <div className="bg-error/10 border border-error/30 rounded-2xl p-4 mb-4 flex items-start gap-2 text-error">
          <AlertCircle size={18} className="mt-0.5" />
          <div className="flex-1">
            <div className="font-bold">{t('rejected_reason', 'سبب الرفض')}</div>
            <div className="text-sm mb-2">{company.rejection_reason}</div>
            <Link to="/company/edit" className="text-error font-semibold underline text-sm">
              {t('fix_and_resubmit', 'تعديل وإعادة التقديم')} →
            </Link>
          </div>
        </div>
      )}
      {company.status === 'suspended' && (
        <div className="bg-warning/10 border border-warning/30 rounded-2xl p-4 mb-4 flex items-start justify-between gap-2 text-warning">
          <div className="flex items-start gap-2">
            <Pause size={18} className="mt-0.5" />
            <div>
              <div className="font-bold">{t('company_suspended', 'الشركة موقوفة مؤقتاً')}</div>
              <div className="text-sm">{t('suspended_hint', 'تواصل مع الإدارة لمراجعة الحالة')}</div>
            </div>
          </div>
          <a
            href="https://wa.me/971544446733"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 bg-warning text-white rounded-lg px-3 py-1.5 text-sm font-semibold whitespace-nowrap"
          >
            <MessageCircle size={14} /> {t('contact_admin', 'تواصل مع الإدارة')}
          </a>
        </div>
      )}

      {/* KYC Section (visible while draft / rejected) */}
      {(company.status === 'draft' || company.status === 'rejected') && (
        <div className="bg-surface border border-border rounded-2xl p-5 mb-4">
          <h2 className="font-bold text-text-primary mb-3 flex items-center gap-2">
            <FileText size={18} /> {t('kyc_docs', 'وثائق KYC')}
          </h2>
          <div className="space-y-2">
            {required.map((kind) => {
              const doc = (company.kyc_docs || []).find((d) => d.kind === kind);
              return <KycRow key={kind} kind={kind} doc={doc} companyId={company.id} required />;
            })}
            {/* Optional bank certificate */}
            <KycRow
              kind="bank_certificate"
              doc={(company.kyc_docs || []).find((d) => d.kind === 'bank_certificate')}
              companyId={company.id}
              required={false}
            />
          </div>
          {company.status === 'draft' && (
            <Button
              onClick={handleSubmit}
              loading={submitMut.isPending}
              disabled={!allUploaded}
              fullWidth
              className="mt-4"
            >
              <Send size={16} /> {allUploaded ? t('submit_for_review', 'تقديم للمراجعة') : t('upload_all_docs_first', 'ارفع كل الوثائق المطلوبة أولاً')}
            </Button>
          )}
        </div>
      )}

      {/* Approved: capability-conditional action grid */}
      {company.status === 'approved' && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <ActionTile to="/company/members" icon={Users} label={t('members', 'الأعضاء')} />
          <ActionTile to="/shipments/create" icon={Send} label={t('create_branded_shipment', 'شحنة باسم الشركة')} />

          {/* International operator → container organizer */}
          {caps.has('container_organizer') && (
            <>
              <ActionTile to="/containers/my" icon={Ship} label={t('my_containers', 'حاوياتي')} highlight />
              <ActionTile to="/containers/create" icon={Plus} label={t('new_container', 'إنشاء حاوية')} />
              <ActionTile to="/origin-warehouses" icon={Warehouse} label={t('origin_warehouses', 'مستودعات المنشأ')} />
            </>
          )}
          {/* Warehouse operator capability → inbound page */}
          {caps.has('origin_warehouse_operator') && (
            <ActionTile
              to="/warehouse/inbound"
              icon={Warehouse}
              label={t('warehouse_inbound', 'استقبال شحنات المستودع')}
              highlight
            />
          )}
          {/* Local operator → trips */}
          {caps.has('local_freight_operator') && (
            <>
              <ActionTile to="/trips" icon={Truck} label={t('my_trips', 'رحلاتي')} />
              <ActionTile to="/trips/create" icon={Plus} label={t('new_trip', 'رحلة جديدة')} />
            </>
          )}
          <ActionTile to="/earnings" icon={DollarSign} label={t('earnings', 'الأرباح')} />
          <ActionTile to="/company/account" icon={DollarSign} label={t('company_account_tile', 'الحساب المالي')} />
          <ActionTile to="/company/customers" icon={Users} label={t('customers_tile', 'الزبائن')} />
          <ActionTile to="/company/reviews" icon={MessageCircle} label={t('reviews_tile', 'التقييمات')} />
          <ActionTile to="/company/pricing-presets" icon={DollarSign} label={t('pricing_presets_tile', 'استراتيجيات التسعير')} />
          <ActionTile to="/company/sub-mode" icon={Layers} label={t('submode_tile', 'وضع التشغيل')} />
          <ActionTile to="/company/kyc-docs" icon={ShieldCheck} label={t('kyc_docs_tile', 'وثائق KYC')} />
          <ActionTile to="/company/edit" icon={Edit} label={t('edit_company_info', 'تعديل البيانات')} />
        </div>
      )}

      {/* Pending / suspended: only edit access */}
      {(company.status === 'pending_approval' || company.status === 'suspended') && (
        <div className="grid grid-cols-2 gap-3">
          <ActionTile to="/company/edit" icon={Edit} label={t('edit_company_info', 'تعديل البيانات')} />
        </div>
      )}
    </AppLayout>
  );
}

function KycRow({ kind, doc, companyId, required }: {
  kind: KycDocKind; doc?: CompanyKycDoc; companyId: number; required: boolean;
}) {
  const { t } = useTranslation();
  const upload = useUploadKycDoc(companyId);
  const [url, setUrl] = useState('');
  const [editing, setEditing] = useState(false);

  async function save() {
    const u = url.trim();
    if (!u) return;
    try {
      await upload.mutateAsync({ kind, fileUrl: u });
      setEditing(false);
      setUrl('');
    } catch (e: any) {
      alert(e?.message || 'فشل الرفع');
    }
  }

  return (
    <div className="flex items-center justify-between gap-2 py-2 border-b border-border last:border-b-0">
      <div className="flex items-center gap-2">
        {doc ? <CheckCircle size={16} className="text-success" /> : <AlertCircle size={16} className={required ? 'text-warning' : 'text-text-tertiary'} />}
        <span className="text-sm text-text-primary">{t(`kyc.${kind}`, DOC_LABELS[kind])}</span>
        {!required && <span className="text-xs text-text-tertiary">({t('optional', 'اختيارية')})</span>}
      </div>
      {!doc && !editing && (
        <button onClick={() => setEditing(true)} className="text-primary text-sm hover:underline">
          {t('upload', 'رفع')}
        </button>
      )}
      {editing && (
        <div className="flex items-center gap-2">
          <input
            dir="ltr"
            placeholder="https://…"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="border border-border rounded-lg px-2 py-1 text-xs"
          />
          <button onClick={save} disabled={upload.isPending} className="text-primary text-sm hover:underline">
            {t('save', 'حفظ')}
          </button>
          <button onClick={() => { setEditing(false); setUrl(''); }} className="text-text-tertiary text-sm">×</button>
        </div>
      )}
      {doc && (
        <a href={doc.file_url} target="_blank" rel="noreferrer" className="text-primary text-xs hover:underline" dir="ltr">
          {t('view', 'عرض')}
        </a>
      )}
    </div>
  );
}

function ActionTile({ to, icon: Icon, label, highlight }: { to: string; icon: typeof Building2; label: string; highlight?: boolean }) {
  return (
    <Link
      to={to}
      className={`rounded-xl p-4 flex flex-col items-start gap-2 hover:opacity-90 transition border ${
        highlight
          ? 'bg-secondary text-white border-secondary'
          : 'bg-surface text-text-primary border-border hover:border-primary'
      }`}
    >
      <Icon size={22} className={highlight ? 'text-white' : 'text-primary'} />
      <span className="font-semibold text-sm">{label}</span>
    </Link>
  );
}
